import {
  getRegion,
  getPlatformFromUrl,
  isGovPartition,
} from '@adsk/acs-sc-web-platform-api';

import { clearAnalyticsIdentity } from './analyticsIdentity';
import loadSegment, { SegmentAnalytics } from './loadSegment';
import segmentApiKey from './segmentApiKey';
import { Properties, PropertyValue } from '../analyticsTypes';
import { NODE_ENV } from '../env';
import MockAnalyticsStore from '../MockAnalyticsStore';
import sanitizeTraits from '../sanitizeTraits';

// Determine the project UID from the URL
const PROJECT_REGEX =
  /\/projects\/([\da-f]{8}-[\da-f]{4}-[\da-f]{4}-[\da-f]{4}-[\da-f]{12})/;
const REGION = getRegion();

function getProjectUidFromWindowLocation(): string | null {
  const url = new URL(window.location.href);
  const match = PROJECT_REGEX.exec(url.pathname);
  if (match) return match[1];
  return null;
}

function addProjectUidToProperties(properties: Properties): Properties {
  const projectUid = getProjectUidFromWindowLocation();
  const platform = getPlatformFromUrl().replace('bim360', 'b360');

  return {
    user_type: platform,
    project_type: platform,
    data_center: REGION,
    screen_height: window.innerHeight,
    screen_width: window.innerWidth,
    ...(projectUid && { project_id: projectUid }),
    ...properties,
  };
}

export type Analytics = SegmentAnalytics & {
  _isInitialized: () => boolean;
  _queuedEvents: Parameters<Analytics['track']>[];
  _queuedIdentifies: Parameters<Analytics['identify']>[];
  _waitForBaseProperties: string[] | null;
  _tryToFlushQueues: () => void;
  resetIdentity: () => void;
  setBaseProperties: (properties: Properties) => void;
  getBaseProperty: (propertyName: string) => PropertyValue;
  waitForBaseProperties: (...propertyNames: string[]) => void;
  load: () => Promise<void>;
};

const analytics: Analytics = {
  _queuedEvents: [],
  _queuedIdentifies: [],
  _waitForBaseProperties: null,
  _isInitialized: () => !!window.analytics?.initialized,
  resetIdentity() {
    // Never throws and guards each step separately: callers navigate immediately
    // after, so an escaping exception (or one `SecurityError` skipping later steps)
    // would leave the user signed in — on IdleTimer, someone who walked away from
    // their machine.
    let failures: unknown[] = [];
    const attempt = (step: () => void) => {
      try {
        step();
      } catch (error) {
        failures = [...failures, error];
      }
    };

    // The persisted identity is authoritative. Until Analytics.js loads,
    // `window.analytics` is the snippet stub whose methods only queue for replay, so
    // going through Segment would clear nothing at the moment this matters most.
    attempt(clearAnalyticsIdentity);

    // Anything queued before Analytics.js was ready is replayed by
    // `_tryToFlushQueues()` on `alloyAnalyticsLoaded`. Both queues only ever have
    // contents while Segment is still loading, so nothing in them has reached the
    // network yet — a queued `identify(previous)` would write the identity straight
    // back once Segment comes up, and a queued `track()` would go out already
    // stamped with it. Safe to drop both before either can replay under the wrong
    // identity.
    attempt(() => {
      analytics._queuedIdentifies = [];
      analytics._queuedEvents = [];
    });

    // Once loaded, Segment holds the identity in memory too and would write it back.
    //
    // Only when loaded, though: calling the stub queues a reset that replays after
    // Analytics.js comes up, which can land after the *next* user's `identify()` and
    // clear their identity instead.
    //
    // Drops `ajs_anonymous_id` as well — Segment's `reset()` always does. Preserving
    // it needs the id captured and restored around this call.
    attempt(() => {
      if (analytics._isInitialized() && window.analytics?.reset) {
        window.analytics.reset();
      }
    });

    if (NODE_ENV === 'development') {
      console.log(
        '%c[analytics.resetIdentity]',
        'color:cornflowerblue',
        `initialized=${analytics._isInitialized()}`,
        // Otherwise a reset that partly failed is indistinguishable from one that
        // worked, even locally.
        ...(failures.length ? ['failed:', failures] : []),
      );
    }
  },
  setBaseProperties(newProperties: Properties) {
    window.accAnalyticsBaseProperties = {
      ...window.accAnalyticsBaseProperties,
      ...newProperties,
    };

    if (NODE_ENV === 'development') {
      console.log(
        '%c[analytics.setBaseProperties] %cprops=',
        'color:cornflowerblue',
        'color:slategrey',
        window.accAnalyticsBaseProperties,
      );
    }

    analytics._tryToFlushQueues();
  },
  getBaseProperty(propertyName) {
    return window.accAnalyticsBaseProperties?.[propertyName];
  },
  waitForBaseProperties(...propertyNames: string[]) {
    analytics._waitForBaseProperties = propertyNames;

    if (NODE_ENV === 'development') {
      console.log(
        '%c[analytics.waitForBaseProperties] %cprops=',
        'color:cornflowerblue',
        'color:slategrey',
        analytics._waitForBaseProperties,
      );
    }
  },
  _tryToFlushQueues() {
    if (!analytics._isInitialized()) {
      return;
    }

    if (
      analytics._waitForBaseProperties?.some(
        (prop) =>
          !(
            window.accAnalyticsBaseProperties &&
            prop in window.accAnalyticsBaseProperties
          ),
      )
    ) {
      return;
    }

    analytics._waitForBaseProperties = null;

    for (const [userAnalyticsId, traits] of analytics._queuedIdentifies) {
      analytics.identify(userAnalyticsId, traits);
    }

    analytics._queuedIdentifies = [];

    for (const [event, properties] of analytics._queuedEvents) {
      analytics.track(event, properties);
    }

    analytics._queuedEvents = [];
  },
  track(event: string, properties?: Properties) {
    // No-op on Afg before the queue branch, so GovCloud never accumulates events.
    if (isGovPartition()) return;

    if (
      analytics._waitForBaseProperties ||
      !analytics._isInitialized() ||
      !window?.analytics?.track
    ) {
      if (NODE_ENV === 'development') {
        console.log(
          '%c[analytics.track%c[QUEUED - NOT SENT]%c] %cevent=%c' +
            event +
            ' %cprops=',
          'color:cornflowerblue',
          'color:red',
          'color:cornflowerblue',
          'color:slategrey',
          'color:cornflowerblue;font-weight:600',
          'color:slategrey',
          properties,
        );
      }

      analytics._queuedEvents = [
        ...analytics._queuedEvents,
        [event, properties],
      ];

      return;
    }

    properties = addProjectUidToProperties({
      ...window.accAnalyticsBaseProperties,
      ...properties,
    });

    if (NODE_ENV === 'production') {
      window.analytics.track(event, properties);
    } else if (NODE_ENV === 'test') {
      MockAnalyticsStore._recordTrack(event, properties);
    } else {
      console.log(
        '%c[analytics.track] %cevent=%c' + event + ' %cprops=',
        'color:cornflowerblue',
        'color:slategrey',
        'color:cornflowerblue;font-weight:600',
        'color:slategrey',
        properties,
      );
    }
  },
  identify(userAnalyticsId: string, traits?: Properties) {
    // No-op on Afg before the queue branch, so GovCloud never accumulates identifies.
    if (isGovPartition()) return;

    if (!analytics._isInitialized() || !window?.analytics?.identify) {
      if (NODE_ENV === 'development') {
        console.log(
          '%c[analytics.identify%c[QUEUED - NOT SENT]%c] %cuserAnalyticsId=%c' +
            userAnalyticsId +
            ' %ctraits=',
          'color:cornflowerblue',
          'color:red',
          'color:cornflowerblue',
          'color:slategrey',
          'color:cornflowerblue;font-weight:600',
          'color:slategrey',
          traits,
        );
      }

      analytics._queuedIdentifies = [
        ...analytics._queuedIdentifies,
        [userAnalyticsId, traits],
      ];

      return;
    }

    const sanitizedTraits = sanitizeTraits(traits);

    if (NODE_ENV === 'production') {
      window.analytics.identify(userAnalyticsId, sanitizedTraits);
    } else if (NODE_ENV === 'test') {
      MockAnalyticsStore._recordIdentify(userAnalyticsId, sanitizedTraits);
    } else {
      if (sanitizedTraits)
        console.log(
          '%c[analytics.identify] %cuserAnalyticsId=%c' +
            userAnalyticsId +
            ' %ctraits=',
          'color:cornflowerblue',
          'color:slategrey',
          'color:cornflowerblue;font-weight:600',
          'color:slategrey',
          sanitizedTraits,
        );
      else
        console.log(
          '%c[analytics.identify] %userAnalyticsId=%c' + userAnalyticsId,
          'color:cornflowerblue',
          'color:slategrey',
          'color:cornflowerblue;font-weight:600',
        );
    }
  },
  load() {
    if (NODE_ENV === 'test') {
      MockAnalyticsStore._recordLoad();
    }
    return new Promise((resolve) => {
      loadSegment(segmentApiKey(), () => {
        if (NODE_ENV === 'development') {
          console.log('%c[analytics.load]', 'color:cornflowerblue');
        }
        if (NODE_ENV === 'test') {
          // in production done by segment script
          window.analytics = window.analytics || {};
          window.analytics.initialized = true;
        }
        const event = new CustomEvent('alloyAnalyticsLoaded');
        window.dispatchEvent(event);

        resolve();
      });
    });
  },
};

window.addEventListener(
  'alloyAnalyticsLoaded',
  () => {
    analytics._tryToFlushQueues();
  },
  { once: true },
);

if (!window.skipAlloyAnalyticsLoad) {
  void analytics.load();
}
export default analytics;
